import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchUserAttributes } from '@aws-amplify/auth';
import { ChevronsUpDown, LogOut, Moon, Sun } from 'lucide-react';
import { useUserSettingsContext } from '../../../context/UserSettingsContext';
import { useTheme } from '../../../context/ThemeContext';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '../../ui/sidebar';
import { itemMatches } from '../../../config/navConfig';
import { signOutUser } from './signOutUser';

/** Sidebar footer account card → Settings pages, theme, sign out. */
export default function NavUser({ settings, pathname }) {
  const { isMobile } = useSidebar();
  const navigate = useNavigate();
  const { userSettings } = useUserSettingsContext();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [email, setEmail] = useState('');

  useEffect(() => {
    let alive = true;
    fetchUserAttributes()
      .then((attrs) => { if (alive) setEmail(attrs?.email || ''); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const displayEmail = email || userSettings?.email || '';
  const name = `${userSettings?.firstName || ''} ${userSettings?.lastName || ''}`.trim()
    || userSettings?.name || displayEmail || 'Account';
  const initials = name.split(/\s+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || 'U';

  const avatar = (
    <Avatar className="h-8 w-8 rounded-lg">
      {userSettings?.profilePicture && <AvatarImage src={userSettings.profilePicture} alt="" />}
      <AvatarFallback className="rounded-lg bg-[var(--sidebar-accent)] text-[var(--sidebar-strong)]">{initials}</AvatarFallback>
    </Avatar>
  );

  const settingsActive = settings ? itemMatches(settings, pathname) : false;

  return (
    <SidebarMenu>
      <SidebarMenuItem data-tour="account">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              isActive={settingsActive}
              className="data-[state=open]:bg-sidebar-accent"
              aria-label="Account menu"
            >
              {avatar}
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium text-[var(--sidebar-strong)]">{name}</span>
                <span className="truncate text-xs text-sidebar-muted">{displayEmail}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4 text-sidebar-muted" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-60"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={8}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                {avatar}
                <div className="grid flex-1 text-left leading-tight">
                  <span className="truncate font-semibold text-[var(--lodge-onyx-900)]">{name}</span>
                  <span className="truncate text-xs">{displayEmail}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {settings && (
              <DropdownMenuGroup>
                <DropdownMenuLabel className="text-[11px] uppercase tracking-wider">Settings</DropdownMenuLabel>
                {settings.children.map((item) => {
                  const Icon = item.icon;
                  const active = itemMatches(item, pathname);
                  return (
                    <DropdownMenuItem key={item.key} asChild className={active ? 'bg-[var(--lodge-onyx-50)] font-medium' : undefined}>
                      <Link to={item.to}>
                        {Icon && <Icon />}
                        {item.title}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuGroup>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                toggleDarkMode();
              }}
            >
              {isDarkMode ? <Sun /> : <Moon />}
              {isDarkMode ? 'Light mode' : 'Dark mode'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => signOutUser(navigate)}>
              <LogOut />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
